import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useMemo, useRef, useState } from 'react';

import type { PackReviewsResponse } from 'app/typings/core/extranet/reviews/reviews';

import styles from '../ExtranetReviews.module.scss';

import { PackSelector } from './PackSelector/PackSelector';

type ExtranetPackReviewsProps = {
  isMultiPack: boolean;
  packIdsValue: number[];
  setPackIdsValue: (value: number[]) => void;
  reviewsByPack: PackReviewsResponse | null | undefined;
};

const SIZE_OF_PACK_INSIDE_LIST = 287 + 16;

export const ExtranetPackReviews = ({
  isMultiPack,
  packIdsValue,
  setPackIdsValue,
  reviewsByPack,
}: ExtranetPackReviewsProps) => {
  const packsListRef = useRef<HTMLDivElement>(null);
  const packsReviewSummaryRef = useRef<HTMLDivElement>(null);

  const [isOnLeftBorder, setIsOnLeftBorder] = useState(true);
  const [isOnRightBorder, setIsOnRightBorder] = useState(false);

  const onClickScroll = (toLeft: boolean) => {
    packsListRef.current?.scrollBy({
      behavior: 'smooth',
      left: toLeft ? -SIZE_OF_PACK_INSIDE_LIST : SIZE_OF_PACK_INSIDE_LIST,
    });
  };

  const hasScroll = useMemo(() => {
    if (!reviewsByPack?.packs || !packsReviewSummaryRef.current) {
      return false;
    }

    return (
      reviewsByPack.packs.length * SIZE_OF_PACK_INSIDE_LIST >
      packsReviewSummaryRef.current.offsetWidth
    );
  }, [reviewsByPack?.packs]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollLeft, clientWidth, scrollWidth } = e.currentTarget;

    setIsOnLeftBorder(scrollLeft === 0);
    setIsOnRightBorder(scrollLeft + clientWidth === scrollWidth);
  };

  return (
    <div ref={packsReviewSummaryRef} className={styles.packsReviewSummary}>
      {hasScroll && !isOnLeftBorder && (
        <div className={`${styles.scrollButtonContainer} ${styles.scrollLeft}`}>
          <Button
            className={styles.scrollButton}
            onClick={() => onClickScroll(true)}
          >
            <LeftOutlined />
          </Button>
        </div>
      )}
      <div
        ref={packsListRef}
        className={styles.packsList}
        onScroll={handleScroll}
      >
        {isMultiPack &&
          reviewsByPack?.packs.map((pack) => (
            <PackSelector
              key={pack.id}
              pack={pack}
              filterValue={packIdsValue}
              setFilterValue={setPackIdsValue}
            />
          ))}
      </div>
      {hasScroll && !isOnRightBorder && (
        <div
          className={`${styles.scrollButtonContainer} ${styles.scrollRight}`}
        >
          <Button
            className={styles.scrollButton}
            onClick={() => onClickScroll(false)}
          >
            <RightOutlined />
          </Button>
        </div>
      )}
    </div>
  );
};
